import React, { useState, useEffect } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { ProjectTag, CreateProjectTag, UpdateProjectTag } from '../types';
import { projectTagsAPI } from '../services/api';

const ProjectTagsPage: React.FC = () => {
  const [tags, setTags] = useState<ProjectTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTag, setEditingTag] = useState<ProjectTag | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const data = await projectTagsAPI.getProjectTags();
      setTags(data);
      setError(null);
    } catch (err) {
      setError('Failed to load project tags');
      console.error('Error loading project tags:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTagName.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const newTagData: CreateProjectTag = {
        name: newTagName.trim()
      };

      const newTag = await projectTagsAPI.createProjectTag(newTagData);
      setTags(prev => [...prev, newTag]);

      setNewTagName('');
      setError(null);
    } catch (err) {
      setError('Failed to create project tag');
      console.error('Error creating project tag:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTag = async (tag: ProjectTag) => {
    if (!tag.name.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const updateData: UpdateProjectTag = {
        id: tag.id,
        name: tag.name.trim(),
      };

      const updatedTag = await projectTagsAPI.updateProjectTag(tag.id, updateData);
      setTags(prev => prev.map(t => t.id === tag.id ? updatedTag : t));
      setEditingTag(null);
      setError(null);
    } catch (err) {
      setError('Failed to update project tag');
      console.error('Error updating project tag:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    const isConfirmed = await confirm({
      title: 'Delete project tag',
      message: 'Are you sure you want to delete this project tag? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      await projectTagsAPI.deleteProjectTag(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
      setError(null);
    } catch (err) {
      setError('Failed to delete project tag');
      console.error('Error deleting project tag:', err);
    }
  };

  const startEditTag = (tag: ProjectTag) => {
    setEditingTag({ ...tag });
  };

  const cancelEdit = () => {
    setEditingTag(null);
  };

  const updateEditingTag = (field: keyof ProjectTag, value: string) => {
    if (!editingTag) return;
    setEditingTag(prev => prev ? { ...prev, [field]: value } : null);
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '0.75rem'
        }}
      >
        <div className="spinner-border" role="status"></div>
        <p className="mt-0">Loading project tags...</p>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '2rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 style={{
              color: 'var(--text-primary)',
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              Project Tags Management
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
              Create and manage tags for organizing your projects
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Create New Project Tag</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleCreateTag}>
            <div className="mb-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="tag-form-field">
                    <label htmlFor="projectTagName" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="projectTagName"
                      className="form-control"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Enter project tag name"
                      maxLength={100}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!newTagName.trim() || isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Project Tag'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <h5 className="card-title">Existing Project Tags</h5>
          </div>
        </div>
        <div className="card-body">
          {tags.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              color: 'var(--text-muted)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                <i className="fa-solid fa-tags" aria-hidden="true"></i>
              </div>
              <h5>No project tags found</h5>
              <p>Create your first project tag using the form above to start organizing your projects.</p>
            </div>
          ) : (
            <div className="row tags-grid">
              {tags.map(tag => (
                <div key={tag.id} className="col-md-6 col-lg-4 mb-3">
                  <div className="card h-100 tag-card">
                    <div className="card-body">
                      {editingTag && editingTag.id === tag.id ? (
                        <div>
                          <div className="mb-3">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editingTag.name}
                              onChange={(e) => updateEditingTag('name', e.target.value)}
                              maxLength={100}
                              placeholder="Project tag name"
                            />
                          </div>
                          <div className="btn-group btn-group-sm w-100">
                            <button
                              className="btn btn-success"
                              onClick={() => handleUpdateTag(editingTag)}
                              disabled={!editingTag.name.trim() || isSubmitting}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={cancelEdit}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="tag-card-content">
                          <div className="tag-card-header">
                            <span className="tag-name">
                              {tag.name}
                            </span>
                            <div className="btn-group btn-group-sm tag-action-group">
                              <button
                                className="btn btn-outline-warning"
                                onClick={() => startEditTag(tag)}
                                disabled={isSubmitting}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteTag(tag.id)}
                                disabled={isSubmitting}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectTagsPage;
